import React, { useState, useEffect } from 'react';
import {
  Star, Users, GraduationCap, BookOpen, Building2, MapPin, ChevronRight, ChevronLeft,
  ThumbsUp, ThumbsDown, MessageCircle, Send, Check, AlertCircle, Clock, Award,
  DollarSign, Heart, RefreshCw, School, Baby
} from 'lucide-react';

// Import data
import tertiaryData from '../data/tertiary-institutions.json';

const BackButton = ({ goBack, label = "Back" }) => (
  <button onClick={goBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-medium">
    <ChevronLeft className="w-5 h-5" />{label}
  </button>
);

// Generate a simple fingerprint for vote tracking
const getFingerprint = () => {
  const stored = localStorage.getItem('ja-vote-fp');
  if (stored) return stored;
  const fp = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem('ja-vote-fp', fp);
  return fp;
};

// ==================== STAR RATING COMPONENT ====================
const StarRating = ({ value, onChange, label, readOnly = false, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => !readOnly && onChange(star)}
            disabled={readOnly}
            className={`${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
          >
            <Star
              className={`${sizeClasses[size]} ${star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

// ==================== TERTIARY INSTITUTION POLL ====================
export const TertiaryPollCard = ({ institution, onVote, userVote }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [localVote, setLocalVote] = useState(userVote);

  const handleVote = (voteType) => {
    const newVote = localVote === voteType ? null : voteType;
    setLocalVote(newVote);
    onVote(institution.id, newVote);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h3 className="font-bold text-gray-800">{institution.short_name || institution.institution_name}</h3>
          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
            <MapPin className="w-3 h-3" />
            <span>{institution.parish}</span>
            <span className={`text-xs px-2 py-0.5 rounded ${
              institution.sector === 'Public' ? 'bg-brand-100 text-brand-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {institution.sector}
            </span>
          </div>
        </div>

        {/* Quick Vote */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleVote('up')}
            className={`p-2 rounded-lg transition-colors ${
              localVote === 'up'
                ? 'bg-brand-100 text-brand-600'
                : 'bg-gray-100 text-gray-500 hover:bg-brand-50'
            }`}
          >
            <ThumbsUp className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleVote('down')}
            className={`p-2 rounded-lg transition-colors ${
              localVote === 'down'
                ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-500 hover:bg-red-50'
            }`}
          >
            <ThumbsDown className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Quick Features */}
      <div className="flex flex-wrap gap-1 mt-3">
        {institution.has_evening_programmes && (
          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">Evening</span>
        )}
        {institution.has_online_programmes && (
          <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded">Online</span>
        )}
        {institution.accepts_slb_loans && (
          <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded">SLB</span>
        )}
        {institution.mature_student_entry && (
          <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded">Mature Entry</span>
        )}
      </div>

      <button
        onClick={() => setShowDetails(!showDetails)}
        className="text-sm text-purple-600 font-medium mt-3 flex items-center gap-1"
      >
        {showDetails ? 'Hide Details' : 'Rate This Institution'}
        <ChevronRight className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-90' : ''}`} />
      </button>
    </div>
  );
};

// ==================== TERTIARY POLLS PAGE ====================
export const TertiaryPollsPage = ({ setPage, goBack }) => {
  const [votes, setVotes] = useState(() => {
    const stored = localStorage.getItem('ja-tertiary-votes');
    return stored ? JSON.parse(stored) : {};
  });
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const institutions = tertiaryData.data || [];

  const institutionTypes = [
    { id: 'all', label: 'All' },
    { id: 'university', label: 'Universities' },
    { id: 'teachers_college', label: 'Teachers Colleges' },
    { id: 'community_college', label: 'Community Colleges' },
    { id: 'vocational', label: 'Vocational' }
  ];

  const filteredInstitutions = institutions.filter(inst => {
    const matchesType = filterType === 'all' || inst.institution_type === filterType;
    const matchesSearch = searchQuery === '' ||
      inst.institution_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (inst.short_name && inst.short_name.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesType && matchesSearch;
  });

  const handleVote = (institutionId, voteType) => {
    const newVotes = { ...votes };
    if (voteType === null) {
      delete newVotes[institutionId];
    } else {
      newVotes[institutionId] = voteType;
    }
    setVotes(newVotes);
    localStorage.setItem('ja-tertiary-votes', JSON.stringify(newVotes));
  };

  // Calculate vote stats
  const voteStats = {
    total: Object.keys(votes).length,
    upvotes: Object.values(votes).filter(v => v === 'up').length,
    downvotes: Object.values(votes).filter(v => v === 'down').length
  };

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      {/* Hero */}
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-6 text-white mb-6">
        <div className="flex items-center gap-2 text-purple-200 text-sm mb-2">
          <GraduationCap className="w-5 h-5" />
          <span>Community Voice</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-black mb-2">Rate Universities & Colleges</h1>
        <p className="text-purple-100">
          Share your opinion on Jamaica's tertiary institutions. Help others make informed decisions.
        </p>

        {/* Vote Stats */}
        <div className="flex gap-4 mt-4 text-sm">
          <span className="bg-white/20 px-3 py-1 rounded-full">
            {voteStats.total} votes cast
          </span>
          <span className="bg-brand-500/30 px-3 py-1 rounded-full">
            {voteStats.upvotes} positive
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <input
            type="text"
            placeholder="Search institutions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
          />
          <div className="flex gap-2 flex-wrap">
            {institutionTypes.map(type => (
              <button
                key={type.id}
                onClick={() => setFilterType(type.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterType === type.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Institution List */}
      <div className="space-y-3">
        {filteredInstitutions.map(institution => (
          <TertiaryPollCard
            key={institution.id}
            institution={institution}
            onVote={handleVote}
            userVote={votes[institution.id]}
          />
        ))}
      </div>

      {filteredInstitutions.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No institutions found matching your search.</p>
        </div>
      )}

      {/* Write Full Review Link */}
      <div className="mt-8 bg-purple-50 border border-purple-200 rounded-xl p-6 text-center">
        <MessageCircle className="w-10 h-10 text-purple-600 mx-auto mb-3" />
        <h3 className="font-bold text-purple-800 mb-2">Want to Share More?</h3>
        <p className="text-purple-600 text-sm mb-4">
          Write a detailed review about your experience at a university or college.
        </p>
        <button
          onClick={() => setPage('share-sentiment')}
          className="bg-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors"
        >
          Write a Review
        </button>
      </div>
    </div>
  );
};

// ==================== PRIMARY SCHOOL POLL CARD ====================
export const PrimaryPollCard = ({ school, onVote, userVote }) => {
  const [localVote, setLocalVote] = useState(userVote);

  const handleVote = (voteType) => {
    const newVote = localVote === voteType ? null : voteType;
    setLocalVote(newVote);
    onVote(school.id, newVote);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h3 className="font-bold text-gray-800">{school.name}</h3>
          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
            <MapPin className="w-3 h-3" />
            <span>{school.parish}</span>
            {school.school_type && (
              <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded">
                {school.school_type.replace('_', ' ')}
              </span>
            )}
          </div>
        </div>

        {/* Quick Vote */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleVote('up')}
            className={`p-2 rounded-lg transition-colors ${
              localVote === 'up'
                ? 'bg-brand-100 text-brand-600'
                : 'bg-gray-100 text-gray-500 hover:bg-brand-50'
            }`}
          >
            <ThumbsUp className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleVote('down')}
            className={`p-2 rounded-lg transition-colors ${
              localVote === 'down'
                ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-500 hover:bg-red-50'
            }`}
          >
            <ThumbsDown className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Features */}
      <div className="flex flex-wrap gap-1 mt-3">
        {school.has_feeding_programme && (
          <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded">Feeding Programme</span>
        )}
        {school.has_computer_lab && (
          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">Computer Lab</span>
        )}
        {school.special_needs_support && (
          <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded">Special Needs</span>
        )}
      </div>
    </div>
  );
};

// ==================== PRIMARY POLLS PAGE ====================
export const PrimaryPollsPage = ({ setPage, goBack }) => {
  const [votes, setVotes] = useState(() => {
    const stored = localStorage.getItem('ja-primary-votes');
    return stored ? JSON.parse(stored) : {};
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParish, setSelectedParish] = useState('all');

  // Curated primary-school set for the community sentiment polls.
  const sampleSchools = [
    { id: 'wolmers-prep', name: "Wolmer's Preparatory", parish: 'Kingston', school_type: 'preparatory', has_feeding_programme: true, has_computer_lab: true, special_needs_support: false },
    { id: 'mona-prep', name: 'Mona Preparatory', parish: 'St. Andrew', school_type: 'preparatory', has_feeding_programme: false, has_computer_lab: true, special_needs_support: true },
    { id: 'holy-childhood', name: 'Holy Childhood High School (Primary)', parish: 'St. Andrew', school_type: 'primary', has_feeding_programme: true, has_computer_lab: true, special_needs_support: false },
    { id: 'vaz-prep', name: 'Vaz Preparatory', parish: 'St. Andrew', school_type: 'preparatory', has_feeding_programme: true, has_computer_lab: true, special_needs_support: false },
    { id: 'ardenne-prep', name: 'Ardenne Preparatory', parish: 'St. Andrew', school_type: 'preparatory', has_feeding_programme: false, has_computer_lab: true, special_needs_support: false },
    { id: 'sts-peter-paul', name: 'Sts. Peter & Paul Primary', parish: 'Kingston', school_type: 'primary', has_feeding_programme: true, has_computer_lab: true, special_needs_support: false },
    { id: 'alpha-primary', name: 'Alpha Primary School', parish: 'Kingston', school_type: 'primary', has_feeding_programme: true, has_computer_lab: false, special_needs_support: true },
    { id: 'half-way-tree', name: 'Half-Way Tree Primary', parish: 'St. Andrew', school_type: 'primary', has_feeding_programme: true, has_computer_lab: true, special_needs_support: false },
    { id: 'meadowbrook', name: 'Meadowbrook Preparatory', parish: 'St. Andrew', school_type: 'preparatory', has_feeding_programme: false, has_computer_lab: true, special_needs_support: false },
    { id: 'priory-prep', name: 'Priory Preparatory', parish: 'St. Andrew', school_type: 'preparatory', has_feeding_programme: true, has_computer_lab: true, special_needs_support: true }
  ];

  const parishes = ['all', 'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary', 'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland', 'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'];

  const filteredSchools = sampleSchools.filter(school => {
    const matchesParish = selectedParish === 'all' || school.parish === selectedParish;
    const matchesSearch = searchQuery === '' ||
      school.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesParish && matchesSearch;
  });

  const handleVote = (schoolId, voteType) => {
    const newVotes = { ...votes };
    if (voteType === null) {
      delete newVotes[schoolId];
    } else {
      newVotes[schoolId] = voteType;
    }
    setVotes(newVotes);
    localStorage.setItem('ja-primary-votes', JSON.stringify(newVotes));
  };

  // Calculate vote stats
  const voteStats = {
    total: Object.keys(votes).length,
    upvotes: Object.values(votes).filter(v => v === 'up').length,
    downvotes: Object.values(votes).filter(v => v === 'down').length
  };

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      {/* Hero */}
      <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl p-6 text-white mb-6">
        <div className="flex items-center gap-2 text-pink-200 text-sm mb-2">
          <School className="w-5 h-5" />
          <span>Community Voice</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-black mb-2">Rate Prep & Primary Schools</h1>
        <p className="text-pink-100">
          Share your experience with preparatory and primary schools. Help other parents make informed decisions.
        </p>

        {/* Vote Stats */}
        <div className="flex gap-4 mt-4 text-sm">
          <span className="bg-white/20 px-3 py-1 rounded-full">
            {voteStats.total} votes cast
          </span>
          <span className="bg-brand-500/30 px-3 py-1 rounded-full">
            {voteStats.upvotes} positive
          </span>
        </div>
      </div>

      {/* Coming Soon Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-800">Help Us Grow This Directory</h3>
            <p className="text-sm text-amber-700 mt-1">
              We add primary schools on an ongoing basis. Know one we should include?
              Contact us and we'll add it.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <input
            type="text"
            placeholder="Search schools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-pink-500"
          />
          <select
            value={selectedParish}
            onChange={(e) => setSelectedParish(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-pink-500 bg-white"
          >
            <option value="all">All Parishes</option>
            {parishes.slice(1).map(parish => (
              <option key={parish} value={parish}>{parish}</option>
            ))}
          </select>
        </div>
      </div>

      {/* School List */}
      <div className="space-y-3">
        {filteredSchools.map(school => (
          <PrimaryPollCard
            key={school.id}
            school={school}
            onVote={handleVote}
            userVote={votes[school.id]}
          />
        ))}
      </div>

      {filteredSchools.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <School className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No schools found matching your search.</p>
        </div>
      )}

      {/* Write Full Review Link */}
      <div className="mt-8 bg-pink-50 border border-pink-200 rounded-xl p-6 text-center">
        <MessageCircle className="w-10 h-10 text-pink-600 mx-auto mb-3" />
        <h3 className="font-bold text-pink-800 mb-2">Share Your Experience</h3>
        <p className="text-pink-600 text-sm mb-4">
          Write a detailed review about your child's primary school experience.
        </p>
        <button
          onClick={() => setPage('share-sentiment')}
          className="bg-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-pink-700 transition-colors"
        >
          Write a Review
        </button>
      </div>
    </div>
  );
};

export default { TertiaryPollsPage, PrimaryPollsPage };
